/**
 * The advanced settings a new build starts with, remembered per device.
 *
 * Everything the "Paramètres avancés" panel shows, and nothing else: the panel
 * is the contract, so the whitelist below is the list of sections it renders
 * (filters, the exclusion list, passives, and the two scoring toggles). The
 * rest of a GenConfig belongs to one build — the time limit, locked slots, the
 * note, the display mode, the defense share — and copying those into every new
 * build would be a different feature.
 *
 * localStorage rather than the account: these are device preferences, they work
 * for a visitor who never signs in, and nothing about them needs the server.
 * The read/write pair is the seam to swap if they ever move onto the profile.
 *
 * `blacklistDefaultsApplied` is not a setting — it is the marker that says which
 * shipped exclusions have already been offered (see GenConfig). It rides along
 * because a new build seeded from these defaults must not be handed back a chip
 * the user removed here.
 */

import { LEGACY_CFG_KEY_CRIT_DAM } from '../core/GenConfig';

const KEY_GEN_DEFAULTS = 'dofgen_gen_defaults_v1';

export const GEN_DEFAULT_KEYS = [
    'typeFilters',
    'allowLegendary',
    'blacklist',
    'blacklistDefaultsApplied',
    'displayPassiveBonuses',
    'passiveOverrides',
    'allowPower',
    'critDamCountsAsDamage',
    'exoRingsOnly',
    'useBoostsForStats',
    'useBoostsForSpells',
];

/** Blacklist entries are stored as ids; GenConfig resolves them against the catalog. */
function toStoredBlacklist(blacklist) {
    return (blacklist || []).map(entry => (
        typeof entry === 'number' ? entry : (entry?.ankama_id || entry?.id)
    )).filter(Boolean);
}

/** The whitelisted slice of a config object, ready to store or to seed with. */
export function pickGenDefaults(config) {
    const source = config && typeof config.toObject === 'function' ? config.toObject() : (config || {});
    const out = {};
    for (const key of GEN_DEFAULT_KEYS) {
        if (source[key] === undefined) continue;
        out[key] = key === 'blacklist' ? toStoredBlacklist(source[key]) : source[key];
    }
    return out;
}

/**
 * What a new build starts from. `{}` when nothing was ever saved, which is what
 * every creation path passed before this existed.
 */
export function getGenDefaults() {
    try {
        const raw = localStorage.getItem(KEY_GEN_DEFAULTS);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (parsed && parsed.critDamCountsAsDamage === undefined && parsed[LEGACY_CFG_KEY_CRIT_DAM] !== undefined) {
            parsed.critDamCountsAsDamage = parsed[LEGACY_CFG_KEY_CRIT_DAM];
        }
        return parsed && typeof parsed === 'object' ? pickGenDefaults(parsed) : {};
    } catch {
        return {};
    }
}

export function setGenDefaults(config) {
    try {
        localStorage.setItem(KEY_GEN_DEFAULTS, JSON.stringify(pickGenDefaults(config)));
    } catch {
        // Private mode, or a full quota: the panel still works, it just forgets.
    }
}

export function clearGenDefaults() {
    try {
        localStorage.removeItem(KEY_GEN_DEFAULTS);
    } catch {
        // As above.
    }
}

export function hasGenDefaults() {
    try {
        return !!localStorage.getItem(KEY_GEN_DEFAULTS);
    } catch {
        return false;
    }
}
